import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { helper } from "@/lib/helper";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  User,
  Phone,
  CreditCard,
  MapPin,
  Users,
  Calendar,
  Search,
  RotateCcw,
  Loader2,Clock3,FileText
} from "lucide-react";
import { ActionCustomerMenu } from "@/components/Holizontal/ActionCustomerMenu";
import { useNavigate, useLocation } from "react-router-dom";
import { RowActionMenu } from "@/components/RowActionMenu";
import { hasPerm } from "@/lib/auth";
import { Button } from "@/components/ui/button";

/** ✅ Small reusable UI bits */
function Chip({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="text-xs font-medium text-muted-foreground">{children}</div>;
}

type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type LeadRow = {
  id: number;
  fname?: string;
  sname?: string;
  phone_number?: string;

  account_status?: number | string;
  national_id?: number;

  BRANCH?: string;
  USERNAME?: string;
  USERNAME2?: string;

  CREATEDBY?: string;
  regDate?: string;
  created_at?: string;
};

type ApiResponse = {
  data: LeadRow[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
  anchor_id: number | null;
  branches: Branch[];
};

const PER_PAGE = 10;
const API_PATH = "/actions/customers/pending";

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function PendingCustomerProfile() {
  // filters
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);
  const [branchId, setBranchId] = useState("");
  const [bdoId, setBdoId] = useState("");

  const { t } = useTranslation();
  // data
  const [loading, setLoading] = useState(false);
  const [bdosLoading, setBdosLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<LeadRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);
    const canCanChangeStatus = hasPerm("review-status");

    type ActionKey =
   
    | "change-status"
  ;

const [activeAction, setActiveAction] = useState<ActionKey | null>(null);
     const openAction = async (k: ActionKey) => {
   // if (k === "apply-loan") {
     // await ensureLoanProductsLoaded();
   // }
    setActiveAction(k);
  };
  const closeAction = () => setActiveAction(null);

  // ✅ scroll-to-top targets
  const tableTopRef = useRef<HTMLDivElement | null>(null);
  const scrollToTableTop = (behavior: ScrollBehavior = "smooth") => {
    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({ behavior, block: "start" });
    });
  };

  // ✅ detect if paging was clicked (so we can scroll AFTER data loads too)
  const pagingScrollRef = useRef<"prev" | "next" | null>(null);

  // reset paging when filters change
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [qDebounced, branchId, bdoId]);

  // ✅ branch-first: load BDOs ONLY when branch selected
  useEffect(() => {
    setBdoId("");
    setBdos([]);
    setError(null);

    if (!branchId) return;

    let alive = true;
    (async () => {
      setBdosLoading(true);
      try {
        const list = await apiFetch<Bdo[]>(`/actions/customers/bdos/${branchId}`);
        if (!alive) return;
        setBdos(list ?? []);
      } catch (e: any) {
        if (!alive) return;
        setBdos([]);
        setError((prev) => prev ?? (e?.message ?? "Failed to load BDOs"));
      } finally {
        if (!alive) return;
        setBdosLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (branchId) params.set("branch_id", branchId);
    if (branchId && bdoId) params.set("bdo_id", bdoId);

    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [qDebounced, branchId, bdoId, cursor, anchorId]);

  async function load() {
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(`${API_PATH}?${queryString}`);

      setRows(json.data ?? []);
      setBranches(json.branches ?? []);

      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);

      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  // ✅ Scroll top AFTER new data finishes loading (only when user clicked Prev/Next)
  useEffect(() => {
    if (loading) return;
    if (!pagingScrollRef.current) return;

    // After render with new rows
    const id = requestAnimationFrame(() => {
      scrollToTableTop("smooth");
      pagingScrollRef.current = null;
    });

    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows]);

  const resetAll = () => {
    setQ("");
    setBranchId("");
    setBdoId("");
    setBdos([]);
    setCursor(null);
    setAnchorId(null);
    scrollToTableTop("smooth");
  };

  // ✅ click Prev/Next: scroll immediately + mark that paging happened + set cursor
  const goPrev = () => {
    if (!prevCursor || loading) return;
    pagingScrollRef.current = "prev";
    scrollToTableTop("smooth");
    setCursor(prevCursor); // triggers load via queryString
  };

  const goNext = () => {
    if (!nextCursor || loading) return;
    pagingScrollRef.current = "next";
    scrollToTableTop("smooth");
    setCursor(nextCursor); // triggers load via queryString
  };

  const nameOf = (r: LeadRow) => {
    const n = `${r.fname ?? ""} ${r.sname ?? ""}`.trim();
    return n || `#${r.id}`;
  };

  const bdoLabel = (r: LeadRow) => (r.USERNAME ?? "-") + (r.USERNAME2 ? ` & ${r.USERNAME2}` : "");

  const navigate = useNavigate();
  const location = useLocation();
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const openCustomerProfile = (id: number | string) => {
    const selectedId = String(id);
    sessionStorage.setItem("selectedCustomer", selectedId);

    navigate("/customer-profile", {
      replace: location.pathname === "/customer-profile",
      state: { selectedCustomer: selectedId, nonce: Date.now() },
    });
  };

  const onEdit = (r: LeadRow) => alert(`Edit incomplete customer_id: ${r.id}`);

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      {/* ✅ top loading bar */}
     

      {/* ===== Header ===== */}
      
     <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Clock3 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight"> {t("pending.title")}</h1>
              <p className="text-xs text-muted-foreground">Pending Customer Listing </p>
            </div>
          </div>

          <button
            onClick={resetAll}
            className="h-10 px-4 rounded-md border border-border hover:bg-muted text-sm font-medium inline-flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
        </div>
      </header>

      <ActionCustomerMenu />

       <div className={cn("h-1 w-full overflow-hidden rounded-full bg-muted", loading ? "opacity-100" : "opacity-0")}>
        <div className="h-full w-1/3 animate-pulse bg-primary" />
      </div>

      {/* ===== Stats row ===== */}
      <div className="flex flex-wrap gap-2">
        <Chip label="Rows" value={rows.length} />
        <Chip label="Anchor" value={anchorId ?? "-"} />
        {loading && (
          <Chip
            label="Status"
            value={
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </span>
            }
          />
        )}
      </div>

      {/* ===== Filters ===== */}
      <div className="rounded-xl border border-border bg-card">
        <div className="p-3 sm:p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Search */}
            <div className="space-y-1">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Name / phone / town…"
                  className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            {/* Branch */}
            <div className="space-y-1">
              <Label>Branch</Label>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All Branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            {/* BDO */}
            <div className="space-y-1">
              <Label>BDO</Label>
              <select
                value={bdoId}
                onChange={(e) => setBdoId(e.target.value)}
                disabled={!branchId || bdosLoading}
                className={cn(
                  "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                  (!branchId || bdosLoading) && "opacity-60 cursor-not-allowed"
                )}
              >
                <option value="">
                  {!branchId ? "Select branch first" : bdosLoading ? "Loading BDOs..." : "All BDOs"}
                </option>
                {bdos.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>

              {bdosLoading && (
                <div className="text-xs text-muted-foreground inline-flex items-center gap-2 pt-1">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading BDOs...
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* ===== Listing ===== */}
      <div className="rounded-xl border border-border bg-card relative">
        {/* ✅ overlay loader */}
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-xl">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading customers...
            </div>
          </div>
        )}

        <div className="p-3 sm:p-4">
          {/* ✅ target for scroll-to-top */}
          <div ref={tableTopRef} />

          {/* Desktop Table */}
          <div className="hidden md:block">
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold">Branch</th>
                    <th className="px-4 py-3 font-semibold">BDO</th>
                    <th className="px-4 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 font-semibold">Date Joined</th>
                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {!loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                        No incomplete profile found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>

                        <td className="px-4 py-3">
                          <div className="font-medium">{r.USERNAME ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{r.USERNAME2 ?? ""}</div>
                          <div className="text-xs text-muted-foreground">Created by: {r.CREATEDBY ?? "-"}</div>
                        </td>

                        <td className="px-4 py-3">
                          <button
                            className="font-semibold text-primary hover:underline text-left"
                            onClick={() => openCustomerProfile(r.id)}
                          >
                            {nameOf(r)}
                          </button>
                          <div className="text-xs text-muted-foreground">{r.phone_number ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{r.national_id ?? "-"}</div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-medium">{r.regDate ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{helper.compactDateTime(r.created_at) ?? "-"}</div>
                        </td>

                        <td className="px-4 py-3 text-right">
                          
                          <div className="flex items-center gap-2">
  <RowActionMenu
    isRTL={
      (document.documentElement.getAttribute("dir") as "ltr" | "rtl") === "rtl"
    }
    onView={() => openCustomerProfile(r.id)}
    onEdit={() => onEdit(r)}
  />

  {canCanChangeStatus && (
    <Button
      variant="secondary"
      onClick={() => openAction("change-status")}
      className="gap-2"
    >
      <FileText className="h-4 w-4" />
      Approve Customer or Reject
    </Button>
  )}
</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2">
            {!loading && rows.length === 0 ? (
              <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
                No incomplete profile found.
              </div>
            ) : (
              rows.map((r) => (
                <div key={r.id} className="rounded-xl border border-border bg-background p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <button
                        className="font-semibold truncate text-primary hover:underline text-left inline-flex items-center gap-2"
                        onClick={() => openCustomerProfile(r.id)}
                      >
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        {nameOf(r)}
                      </button>

                      <div className="mt-2 text-xs text-muted-foreground grid gap-1">
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{r.phone_number ?? "-"}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <CreditCard className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{r.national_id ?? "-"}</span>
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground grid gap-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{r.BRANCH ?? "-"}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{bdoLabel(r)}</span>
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          Joined: {r.regDate ?? "-"} • {helper.compactDateTime(r.created_at) ?? "-"}
                        </span>
                      </div>
                    </div>

                    <div className="shrink-0">
                   <div className="flex items-center gap-2">
  <RowActionMenu
    isRTL={
      (document.documentElement.getAttribute("dir") as "ltr" | "rtl") === "rtl"
    }
    onView={() => openCustomerProfile(r.id)}
    onEdit={() => onEdit(r)}
  />

  {canCanChangeStatus && (
    <Button
      variant="secondary"
      onClick={() => openAction("change-status")}
      className="gap-2"
    >
      <FileText className="h-4 w-4" />
      Approve Customer or Reject
    </Button>
  )}
</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ✅ Bottom pagination (scrolls top on click + after data loads) */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}